import type { Dispatch } from 'redux';
import {
    PrivateDistributionList,
    DistributionListMember,
    createDataActionFactory,
    Feature,
    makeHubPersonaId,
    PersonType,
    createActionFactory,
} from '../../../shared';
import { createPrivateDL as owsCreatePrivateDL } from '../../dataLayer/ows/createPrivateDL';
import {
    updatePrivateDL as owsUpdatePrivateDL,
    addMembersToPrivateDL as owsAddMembersToPrivateDL,
    removeMembersFromPrivateDL as owsRemoveMembersFromPrivateDL,
} from '../../dataLayer/ows/updatePrivateDL';

import {
    getMailBoxTypeFromPDLMember,
    getKeysFromPDLMemberEmailWrapper,
} from '../../utils/loggingUtils';
import { getSuggestedPeopleType } from '../../dataLayer/searchFunctionality/getFilteredPeople';
import {
    convertPersonaTypeToPrivateDLMemberSuggestion,
    createManuallyTypedEntry,
} from '../../dataLayer/ows/converters/pdlMemberConverter';
import { now } from 'owa-datetime';

const defaultGetProperties = action => {
    return null;
};

const operationLogGetProperties = action => {
    const { operationLog } = action.payload;
    return {
        operationLog,
    };
};

export const privateDLCreateCompletedAction = createDataActionFactory<
    {
        privateDL: PrivateDistributionList;
        folderId: string;
    },
    Error & {
        operationLog: string;
    }
>(
    'PrivateDLCreateCompletedAction',
    Feature.PrivateDistributionList,
    defaultGetProperties,
    operationLogGetProperties
);

export const privateDLEditCompletedAction = createDataActionFactory<{}, Error>(
    'PrivateDLEditCompletedAction',
    Feature.PrivateDistributionList
);

export const prefetchMRUMembersStarted = createActionFactory<void>(
    'PrefetchMRUMembersStarted',
    Feature.PrivateDistributionList
);

export const prefetchMRUMembersCompletedAction = createDataActionFactory<
    { distributionListMembersMRU: DistributionListMember[] },
    Error
>('PrefetchMRUMembersCompletedAction', Feature.PrivateDistributionList);

export const privateDLAddMembersCompletedAction = createDataActionFactory<
    {
        hubPersonaId: string;
        addedMembers: DistributionListMember[];
    },
    Error & {
        hubPersonaId: string;
        notAddedMembers: DistributionListMember[];
    } & {
        operationLog: string;
    }
>(
    'PrivateDLAddMembersCompletedAction',
    Feature.ContactListsInCommandBar,
    defaultGetProperties,
    operationLogGetProperties
);

export const privateDLRemoveMembersCompletedAction = createDataActionFactory<
    {
        hubPersonaId: string;
        removedMembers: DistributionListMember[];
    },
    Error & {
        hubPersonaId: string;
        notRemovedMembers: DistributionListMember[];
    } & {
        operationLog: string;
    }
>(
    'PrivateDLRemoveMembersCompletedAction',
    Feature.ContactListsInCommandBar,
    defaultGetProperties,
    operationLogGetProperties
);

export const privateDLUpdateCompletedAction = createDataActionFactory<
    {
        hubPersonaId: string;
        newName: string;
        newNotes: string;
        oldMembers: DistributionListMember[];
        newMembers: DistributionListMember[];
    },
    Error & {
        operationLog: string;
    }
>(
    'PrivateDLUpdateCompletedAction',
    Feature.PrivateDistributionList,
    defaultGetProperties,
    operationLogGetProperties
);

export const updatePrivateDL = (dispatch: Dispatch) => (
    currentList: PrivateDistributionList,
    newName: string,
    newNotes: string,
    newMembers: DistributionListMember[],
    parentFolderId: string
): Promise<void> =>
    owsUpdatePrivateDL({
        personaId: currentList.personaId,
        oldName: currentList.info.listOriginalDisplayName,
        newName,
        oldNotes: currentList.notes,
        newNotes,
        oldMembers: currentList.members,
        newMembers,
        parentFolderId: parentFolderId,
    }).then(
        () => {
            dispatch(
                privateDLUpdateCompletedAction.success({
                    hubPersonaId: currentList.hubPersonaId,
                    newName,
                    newNotes,
                    oldMembers: currentList.members,
                    newMembers: newMembers,
                })
            );
        },
        error => {
            const oldMailBoxTypes = currentList.members.map(getMailBoxTypeFromPDLMember);
            const newMailBoxTypes = newMembers.map(getMailBoxTypeFromPDLMember);
            const oldMembersEmailWrapperFields = currentList.members.map(
                getKeysFromPDLMemberEmailWrapper
            );
            const newMemebersEmailWrapperFields = newMembers.map(getKeysFromPDLMemberEmailWrapper);
            const operationLog = JSON.stringify({
                oldMailBoxTypes,
                newMailBoxTypes,
                oldMembersEmailWrapperFields,
                newMemebersEmailWrapperFields,
            });
            dispatch(privateDLUpdateCompletedAction.error({ ...error, operationLog }));
            throw error;
        }
    );

export const addMembersToPrivateDL = (dispatch: Dispatch) => (
    currentList: PrivateDistributionList,
    membersToAdd: DistributionListMember[]
): Promise<void> =>
    owsAddMembersToPrivateDL({
        personaId: currentList.personaId,
        membersToAdd,
    }).then(
        () => {
            dispatch(
                privateDLAddMembersCompletedAction.success({
                    hubPersonaId: currentList.hubPersonaId,
                    addedMembers: membersToAdd,
                })
            );
        },
        error => {
            const notAddedMembers = membersToAdd.map(getKeysFromPDLMemberEmailWrapper);
            const operationLog = JSON.stringify({
                membersToAdd: notAddedMembers,
            });
            dispatch(
                privateDLAddMembersCompletedAction.error({
                    ...error,
                    hubPersonaId: currentList.hubPersonaId,
                    notAddedMembers: membersToAdd,
                    operationLog,
                })
            );
            throw error;
        }
    );

export const removeEmailsFromPrivateDL = (dispatch: Dispatch) => (
    currentList: PrivateDistributionList,
    emailsToAdd: string[]
): Promise<void> =>
    removeMembersFromPrivateDL(dispatch)(currentList, emailsToAdd.map(createManuallyTypedEntry));

export const removeMembersFromPrivateDL = (dispatch: Dispatch) => (
    currentList: PrivateDistributionList,
    membersToRemove: DistributionListMember[]
): Promise<void> =>
    owsRemoveMembersFromPrivateDL({
        personaId: currentList.personaId,
        membersToRemove,
    }).then(
        () => {
            dispatch(
                privateDLRemoveMembersCompletedAction.success({
                    hubPersonaId: currentList.hubPersonaId,
                    removedMembers: membersToRemove,
                })
            );
        },
        error => {
            const notRemovedMembers = membersToRemove.map(getKeysFromPDLMemberEmailWrapper);
            const operationLog = JSON.stringify({
                membersToRemove: notRemovedMembers,
            });
            dispatch(
                privateDLRemoveMembersCompletedAction.error({
                    ...error,
                    hubPersonaId: currentList.hubPersonaId,
                    notRemovedMembers: membersToRemove,
                    operationLog,
                })
            );
            throw error;
        }
    );

export const editPrivateDL = (dispatch: Dispatch) => (
    currentList: PrivateDistributionList,
    newName: string,
    newNotes: string,
    newMembers: DistributionListMember[],
    contactFolderId: string
): Promise<void> =>
    updatePrivateDL(dispatch)(currentList, newName, newNotes, newMembers, contactFolderId)
        .then(() => {
            dispatch(privateDLEditCompletedAction.success());
        })
        .catch(error => {
            dispatch(privateDLEditCompletedAction.error(error));
            throw error;
        });

export const savePrivateDL = (dispatch: Dispatch) => (
    newName: string,
    newNotes: string,
    newMembers: DistributionListMember[],
    parentFolderId: string
): Promise<string> =>
    owsCreatePrivateDL({
        newName,
        newNotes,
        newMembers,
        parentFolderId,
    }).then(
        newPrivateDLIds => {
            const newPrivateDL: PrivateDistributionList = {
                type: PersonType.PrivateDistributionList,
                itemId: newPrivateDLIds.itemId,
                hubPersonaId: makeHubPersonaId(newPrivateDLIds.personaId),
                personaId: newPrivateDLIds.personaId,
                info: {
                    listOriginalDisplayName: newName,
                    displayNameLastFirst: newName,
                    displayNameFirstLast: newName,
                },
                notes: newNotes,
                members: newMembers,
                // To avoid jumping of the HubEntry while we doing optimistic update in UI, we provide fake creationTime.
                // After server response we overwrite creationTime of the PDL with the correct time.
                creationTime: String(now()),
            };

            dispatch(
                privateDLCreateCompletedAction.success({
                    privateDL: newPrivateDL,
                    folderId: parentFolderId,
                })
            );
            return newPrivateDL.hubPersonaId;
        },
        error => {
            const newMailBoxTypes = newMembers.map(getMailBoxTypeFromPDLMember);
            const newMemebersEmailWrapperFields = newMembers.map(getKeysFromPDLMemberEmailWrapper);
            const operationLog = JSON.stringify({
                newMailBoxTypes,
                newMemebersEmailWrapperFields,
            });
            dispatch(privateDLCreateCompletedAction.error({ ...error, operationLog }));
            throw error;
        }
    );

export const prefetchMRUMembers = (dispatch: Dispatch) => (): Promise<void> => {
    dispatch(prefetchMRUMembersStarted());
    return getSuggestedPeopleType()
        .then(personasMRU => {
            const distributionListMembersMRU: DistributionListMember[] = [];
            personasMRU &&
                personasMRU.forEach(persona => {
                    distributionListMembersMRU.push(
                        ...convertPersonaTypeToPrivateDLMemberSuggestion(persona)
                    );
                });
            dispatch(prefetchMRUMembersCompletedAction.success({ distributionListMembersMRU }));
        })
        .catch(error => {
            dispatch(prefetchMRUMembersCompletedAction.error(error));
            throw error;
        });
};
export type { DataActionFactory } from '../../../shared';
